import { api, type CodeHostConnection, type CodeHostsResponse } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";

export const codeHostsKey = (containerId: string) => ["code-hosts", containerId];

// A Boo's code host connections, as its GitHub/GitLab connection pages, the app-source default
// and the bundle install dialog see them.
export function useCodeHosts(containerId: string, opts: { enabled?: boolean } = {}) {
  const query = useQuery({
    queryKey: codeHostsKey(containerId),
    queryFn: () => api.codeHosts.list(containerId),
    enabled: opts.enabled ?? true,
  });
  const data: CodeHostsResponse | undefined = query.data;
  const active = (data?.connections ?? []).filter((c) => c.status === "active");
  const defaultConnection: CodeHostConnection | null = active.find((c) => c.id === data?.defaultConnectionId) ?? null;
  return { query, data, active, defaultConnection };
}

export const hostLabel = (host: CodeHostConnection["host"]) => (host === "github" ? "GitHub" : "GitLab");

// The instance hostname, only when it isn't the host's own service: the icon
// already says GitHub or GitLab, so "github.com" and "gitlab.com" add nothing.
export const instanceLabel = (conn: Pick<CodeHostConnection, "hostLabel">): string | null =>
  conn.hostLabel === "github.com" || conn.hostLabel === "gitlab.com" ? null : conn.hostLabel;
export const repoNoun = (host: CodeHostConnection["host"]) => (host === "github" ? "repository" : "project");

// Leave the dashboard for a host's install or sign-in page, only over http(s)
// and only to the host we expect.
export function leaveForHost(url: string, expectedHost: string): void {
  const target = new URL(url);
  if (!/^https?:$/.test(target.protocol) || target.host !== expectedHost) throw new Error("Unexpected sign-in address");
  window.location.href = target.toString();
}

// What to tell the user when the browser comes back from a host with
// ?codehost=<outcome>. `kind` picks the toast style.
export function codeHostOutcomeMessage(outcome: string): { kind: "success" | "info" | "error"; text: string } {
  switch (outcome) {
    case "connected":
      return { kind: "success", text: "Code host connected" };
    case "choose_group":
      return { kind: "info", text: "Signed in. Choose the group this connection works in." };
    case "requested":
      return { kind: "info", text: "Install requested. A GitHub org owner must approve it." };
    case "sign_in_required":
      return { kind: "error", text: "Sign in to Boo in this browser, then connect again." };
    case "bound_elsewhere":
      return { kind: "error", text: "That GitHub organization is already connected to another Boo workspace." };
    case "held_by_other_boo":
      return { kind: "error", text: "That GitHub organization is already connected to another Boo here. Share it with the workspace from that Boo, then add it from the workspace." };
    case "denied":
      return { kind: "error", text: "Authorization was declined." };
    default:
      // not_authorized, needs_authorization, error
      return { kind: "error", text: "Couldn't connect. Please try again." };
  }
}
